import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Heart, ShoppingCart } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ProductImageGallery } from './ProductImageGallery';

/**
 * Horizontal swipe-and-scroll product carousel. Uses native CSS scroll-snap so
 * touch swipe works out of the box on mobile. Desktop arrow buttons animate the
 * scroll position. Cards are sized responsively: 1.4 on mobile, 2 on tablet,
 * 3 on desktop, 4 on xl.
 */
export const ProductCarousel = ({
  title,
  subtitle,
  products = [],
  loading = false,
  onProductClick,
  onAddToCart,
  onAddToWishlist,
  testId,
}) => {
  const trackRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const updateButtons = () => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return undefined;
    updateButtons();
    el.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons);
    return () => {
      el.removeEventListener('scroll', updateButtons);
      window.removeEventListener('resize', updateButtons);
    };
  }, [products]);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
  };

  return (
    <section className="py-10 sm:py-14" data-testid={testId}>
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between mb-5 sm:mb-7">
          <div>
            {subtitle && (
              <p className="font-montserrat text-[10px] sm:text-xs tracking-[0.4em] uppercase text-[#8B7D6B] mb-1.5">
                {subtitle}
              </p>
            )}
            <h2 className="font-seasons text-3xl sm:text-4xl text-[#2C2C2C] leading-tight">{title}</h2>
          </div>
          <div className="hidden sm:flex gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              disabled={!canPrev}
              aria-label="Scroll left"
              data-testid={`${testId}-prev`}
              className={`p-2.5 rounded-full border transition ${canPrev ? 'border-[#6B4E37] text-[#6B4E37] hover:bg-[#6B4E37] hover:text-white' : 'border-[#E8E5E0] text-[#D4C4B0] cursor-not-allowed'}`}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              disabled={!canNext}
              aria-label="Scroll right"
              data-testid={`${testId}-next`}
              className={`p-2.5 rounded-full border transition ${canNext ? 'border-[#6B4E37] text-[#6B4E37] hover:bg-[#6B4E37] hover:text-white' : 'border-[#E8E5E0] text-[#D4C4B0] cursor-not-allowed'}`}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex gap-4 overflow-hidden">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex-shrink-0 w-[68%] sm:w-[44%] md:w-[31%] xl:w-[23%] aspect-[3/4] bg-[#F5F3F0] animate-pulse rounded" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <p className="text-sm font-montserrat text-[#8B7D6B] py-6">No products to show yet.</p>
        ) : (
          <div
            ref={trackRef}
            className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 snap-x snap-mandatory scroll-smooth -mx-4 px-4 sm:mx-0 sm:px-0"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            data-testid={`${testId}-track`}
          >
            <style>{`[data-testid="${testId}-track"]::-webkit-scrollbar{display:none;}`}</style>
            {products.map((product) => (
              <article
                key={product.id}
                className="flex-shrink-0 snap-start w-[68%] sm:w-[44%] md:w-[31%] xl:w-[23%] group cursor-pointer"
                data-testid={`carousel-card-${product.id}`}
                onClick={() => onProductClick?.(product)}
              >
                <div className="relative bg-[#F5F3F0] aspect-[3/4] overflow-hidden rounded-sm">
                  <ProductImageGallery
                    images={product.images}
                    fallback={product.image}
                    alt={product.name}
                    compact
                    className="w-full h-full"
                  />
                  {product.discount && (
                    <Badge className="absolute top-3 left-3 z-10 bg-[#6B4E37] text-white font-montserrat text-[10px] tracking-wider px-2 py-0.5">
                      {product.discount}
                    </Badge>
                  )}
                  {product.stock === 0 && (
                    <div className="absolute inset-0 bg-white/60 flex items-center justify-center">
                      <span className="bg-[#2C2C2C] text-white font-montserrat text-[11px] tracking-widest uppercase px-3 py-1.5">Sold out</span>
                    </div>
                  )}
                  {/* Hover actions */}
                  <div className="absolute bottom-2 left-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Button
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); onAddToCart?.(product); }}
                      disabled={product.stock === 0}
                      className="flex-1 bg-white/95 hover:bg-white text-[#2C2C2C] font-montserrat tracking-wider uppercase text-[10px] py-2 h-auto"
                      data-testid={`carousel-add-${product.id}`}
                    >
                      <ShoppingCart className="h-3.5 w-3.5 mr-1.5" /> Add
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); onAddToWishlist?.(product); }}
                      aria-label="Add to wishlist"
                      className="bg-white/95 hover:bg-white border-none text-[#6B4E37] h-auto p-2"
                    >
                      <Heart className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                <div className="pt-3 pb-1">
                  <p className="font-montserrat text-[10px] tracking-[0.25em] uppercase text-[#8B7D6B] truncate">{product.category}</p>
                  <h3 className="font-seasons text-base sm:text-lg text-[#2C2C2C] truncate mt-0.5 group-hover:text-[#6B4E37] transition-colors">
                    {product.name}
                  </h3>
                  <div className="flex items-baseline gap-2 mt-1">
                    <span className="font-seasons text-base text-[#2C2C2C]">₹{product.price?.toLocaleString('en-IN')}</span>
                    {product.originalPrice && product.originalPrice > product.price && (
                      <span className="font-montserrat text-xs text-[#8B7D6B] line-through">₹{product.originalPrice?.toLocaleString('en-IN')}</span>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
